<template>
  <v-navigation-drawer
    v-model="drawer"
    :permanent="!mdAndDown"
    :temporary="mdAndDown"
    :rail="navCollapsed && !mdAndDown"
    :width="260"
    :rail-width="58"
  >
    <v-list class="py-0">
      <v-list-item v-if="!mdAndDown" density="comfortable" @click="toggleNavCollapsed">
        <template v-if="navCollapsed" #prepend>
          <v-icon>mdi-chevron-double-right</v-icon>
        </template>
        <template v-if="!navCollapsed" #append>
          <v-icon>mdi-chevron-double-left</v-icon>
        </template>
      </v-list-item>

      <template v-for="item in primaryNavigationItems" :key="navItemKey(item)">
        <template v-if="isExternalNavItem(item)">
          <v-divider class="my-1" data-testid="nav-pricing-hub-divider" />
          <v-list-item
            :href="item.href"
            target="_blank"
            rel="noopener noreferrer"
            :active="false"
            :prepend-icon="item.icon"
            append-icon="mdi-open-in-new"
            class="ise-nav-interaction"
            data-testid="nav-pricing-hub-link"
          >
            <v-list-item-title>{{ item.label }}</v-list-item-title>
          </v-list-item>
        </template>
        <v-list-item
          v-else
          :to="item.to"
          router
          color="primary"
          :active="isNavItemActive(item)"
          :prepend-icon="item.icon"
          class="ise-nav-interaction"
          :class="{ 'unpublished-changes-item': item.to === '/unpublished' }"
        >
          <v-list-item-title>{{ item.label }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useDisplay } from 'vuetify'
import {
  buildPrimaryNavigationItems,
  isExternalNavItem,
  isRulesSetupNavActive,
  isSettingsNavActive,
} from '@/navigation/navigationItems'
import type { NavItem, PrimaryNavItem } from '@/navigation/types'

const drawer = defineModel<boolean>({ required: true })

const props = defineProps<{
  hasUnpublishedChanges: boolean
  pricingHubHref?: string
}>()

const route = useRoute()
const { mdAndDown } = useDisplay()
const NAV_COLLAPSED_KEY = 'ise.ui.navCollapsed'
const navCollapsed = ref(false)

const primaryNavigationItems = computed(() =>
  buildPrimaryNavigationItems(props.hasUnpublishedChanges, props.pricingHubHref),
)

const navItemKey = (item: PrimaryNavItem): string =>
  isExternalNavItem(item) ? item.href : item.to

const isNavItemActive = (item: NavItem): boolean => {
  if (item.to === '/setup') {
    return isRulesSetupNavActive(route.path)
  }

  if (isSettingsNavActive(item.to)) {
    return isSettingsNavActive(route.path)
  }

  return route.path === item.to
}

const toggleNavCollapsed = () => {
  navCollapsed.value = !navCollapsed.value
}

onMounted(() => {
  try {
    const raw = window.localStorage.getItem(NAV_COLLAPSED_KEY)
    navCollapsed.value = raw === '1'
  } catch {
    // ignore
  }
})

watch(
  () => navCollapsed.value,
  (value) => {
    try {
      window.localStorage.setItem(NAV_COLLAPSED_KEY, value ? '1' : '0')
    } catch {
      // ignore
    }
  },
)
</script>

<style scoped>
.unpublished-changes-item {
  background-color: #fff3e0; /* Light orange background */
  border-left: 4px solid #ff9800; /* Orange accent */
  margin-bottom: 8px;
}

.unpublished-changes-item:hover {
  background-color: #ffe0b2; /* Slightly darker on hover */
}

.v-theme--dark .unpublished-changes-item {
  background-color: #4a3800;
  border-left-color: #ffa726;
}

.v-theme--dark .unpublished-changes-item:hover {
  background-color: #5c4500;
}
</style>
